import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { trackCustomEvent } from "@/lib/metaPixel";
import { devLog } from "@/lib/devLog";

// Business Telegram destination. The `t.me/m/` short link is a
// private-chat entry point Danny Clans generated in Telegram — kept as
// a constant (not env-driven) so the target is bundled into the deployed
// HTML for social/link previews and anchor-inspection crawlers.
const TELEGRAM_URL = "https://t.me/m/SWX0v6OsNDdk";

// Routes where the float should stay hidden — admin surfaces are internal
// tools, not lead-capture surfaces. Kept in sync with WhatsAppFloat.
const HIDDEN_ROUTE_PATTERNS = [/^\/admin/, /^\/admin-clan/];

/**
 * Floating Telegram CTA anchored above the WhatsApp float in the
 * bottom-right of the viewport. Stacks vertically with a stable ~12 px
 * gap so both float buttons remain independently clickable.
 *
 * • Uses the official Telegram brand blue (#229ED9) with a matching
 *   subtle idle pulse ring — visually parallel to WhatsAppFloat so the
 *   two feel like siblings, not a random pair of colours.
 * • Shows an expanded label on desktop hover and on mobile after a
 *   short idle delay (staggered vs WhatsApp so they don't announce
 *   themselves in unison), then collapses back to the icon-only pill.
 * • Fires a Meta Pixel custom event on click for CAPI attribution
 *   (event name `TelegramClick`, distinct from `WhatsAppClick` so the
 *   two channels are attributed separately in reporting).
 * • Fully keyboard-accessible + honours prefers-reduced-motion.
 */
export const TelegramFloat = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [expanded, setExpanded] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const hidden = HIDDEN_ROUTE_PATTERNS.some((re) => re.test(location.pathname));
    setVisible(!hidden);
  }, [location.pathname]);

  // One-time expanded nudge — staggered ~1 s later than WhatsApp's so the
  // two never announce themselves at the same instant. Collapses back
  // symmetrically so the stack returns to compact icon-only rest state.
  useEffect(() => {
    if (!visible) return;
    const reducedMotion =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reducedMotion) return;
    const expandTimer = setTimeout(() => setExpanded(true), 3500);
    const collapseTimer = setTimeout(() => setExpanded(false), 8500);
    return () => {
      clearTimeout(expandTimer);
      clearTimeout(collapseTimer);
    };
  }, [visible]);

  if (!visible) return null;

  const label = t("common.telegramCta", "Chat on Telegram");

  const handleClick = () => {
    // Fire-and-forget analytics — never block the outbound anchor click.
    // Fires a Meta Pixel custom event (`TelegramClick`) so it appears
    // as its own row in Events Manager, mirrored via CAPI for
    // cookieless / iOS 14+ attribution. `trackCustomEvent` logs its
    // own SDK/network failures via devLog; this outer catch protects
    // against unexpected sync throws.
    try {
      trackCustomEvent("TelegramClick", {
        source: "float_button",
        path: location.pathname,
      });
    } catch (err) {
      devLog.warn("TelegramFloat: click tracker threw", err);
    }
  };

  return (
    <a
      href={TELEGRAM_URL}
      target="_blank"
      rel="noopener noreferrer"
      onClick={handleClick}
      onMouseEnter={() => setExpanded(true)}
      onMouseLeave={() => setExpanded(false)}
      onFocus={() => setExpanded(true)}
      onBlur={() => setExpanded(false)}
      aria-label={label}
      data-testid="telegram-float-button"
      className="fixed bottom-[88px] right-5 z-[300] flex items-center gap-2 rounded-full bg-[#229ED9] text-white shadow-[0_10px_30px_-8px_rgba(34,158,217,0.55)] ring-1 ring-black/5 transition-[padding,width,transform,box-shadow] duration-200 hover:scale-[1.03] hover:shadow-[0_16px_40px_-10px_rgba(34,158,217,0.65)] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#229ED9]/40 sm:bottom-[96px] sm:right-6"
      style={{ padding: expanded ? "12px 20px 12px 14px" : "14px" }}
    >
      {/* Idle pulse ring — pauses on hover so the label reads cleanly. */}
      <span
        aria-hidden="true"
        className={`pointer-events-none absolute inset-0 rounded-full ring-2 ring-[#229ED9]/70 ${expanded ? "" : "animate-ping"}`}
        style={{ animationDuration: "2.4s" }}
      />
      {/* Telegram paper-plane glyph — inline SVG to avoid an extra icon dep. */}
      <svg
        aria-hidden="true"
        viewBox="0 0 32 32"
        width="28"
        height="28"
        className="relative shrink-0"
        fill="currentColor"
      >
        <path d="M27.44 4.66 3.53 14.03c-1.63.66-1.62 1.57-.3 1.98l6.13 1.91 14.19-8.95c.67-.41 1.29-.19.78.26L12.83 19.6l-.44 6.35c.63 0 .91-.29 1.24-.63l2.99-2.9 6.19 4.57c1.14.63 1.96.3 2.25-1.05l4.07-19.19c.42-1.66-.62-2.41-1.69-1.99z" />
      </svg>
      <span
        className={`overflow-hidden whitespace-nowrap text-sm font-semibold transition-[max-width,opacity,margin] duration-200 ${
          expanded ? "max-w-[220px] opacity-100 ml-1" : "max-w-0 opacity-0 ml-0"
        }`}
      >
        {label}
      </span>
    </a>
  );
};

export default TelegramFloat;
